import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { useNotifications } from '../notification/notification.hook';
import { gameHubService } from './game-hub.service';
import { GameEvent, GameUiModule } from './game-module';
import { usePlayer } from './player.hook';

export interface GameStateValue<V> {
  view?: V;
  events: GameEvent[];
  isLoaded: boolean;
  isExecuting: boolean;
  load: (lookup: string) => Promise<void>;
  dispatch: (actionCreator: (gameId: string) => unknown) => Promise<void>;
}

const defaultState: GameStateValue<unknown> = {
  events: [],
  isLoaded: false,
  isExecuting: false,
  load: () => Promise.resolve(),
  dispatch: () => Promise.resolve(),
};

const StateContext = createContext<GameStateValue<unknown>>(defaultState);

export function useGameState<V>(): GameStateValue<V> {
  return useContext(StateContext) as GameStateValue<V>;
}

interface GameProviderArgs<V> {
  module: GameUiModule<V>;
  children: React.ReactNode;
}

/**
 * Hosts one game: it joins (or reconnects to) the game once the player has a
 * name, keeps the view the hub service fans out, and sends the actions the
 * game's components dispatch.
 */
function GameProvider<V>({ module, children }: GameProviderArgs<V>) {
  const [gameId, setGameId] = useState('');
  const [game, setGame] = useState<{ view?: V; events: GameEvent[] }>({
    events: [],
  });
  const [isExecuting, setExecuting] = useState(false);
  const { playerName } = usePlayer();

  const { error } = useNotifications();

  useEffect(() => {
    const subscriber = gameHubService.subscribeToUpdates(
      (view: unknown, events: GameEvent[], err?: string) => {
        if (err) {
          error(err);
        }

        // The service fans out the view of the game this provider registered.
        setGame({ view: view as V, events });
      },
    );

    return () => gameHubService.unsubscribeToUpdates(subscriber);
  }, [error]);

  const load = useCallback(
    async (lookup: string) => {
      if (!playerName) {
        return;
      }

      const game = await gameHubService.getGameByLookup(lookup);
      setGameId(game.id);
      gameHubService.registerGame(game.id, module);

      await gameHubService.initialiseConnection();

      await (game.hasJoined
        ? gameHubService.reconnectToGame(game.id)
        : gameHubService.joinGame(game.id, playerName));
    },
    [module, playerName],
  );

  const dispatch = useCallback(
    async (actionCreator: (id: string) => unknown) => {
      try {
        setExecuting(true);
        await gameHubService.executeAction(actionCreator(gameId));
      } catch (err) {
        error(err instanceof Error ? err.message : String(err));
      } finally {
        setExecuting(false);
      }
    },
    [gameId, error],
  );

  const state: GameStateValue<V> = {
    view: game.view,
    events: game.events,
    isLoaded: game.view !== undefined,
    isExecuting,
    load,
    dispatch,
  };

  return (
    <StateContext.Provider value={state}>{children}</StateContext.Provider>
  );
}

export default GameProvider;
